import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  TextField,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  MusicNote as TikTokIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const TikTokAccountSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data from OAuth
  const [userInfo, setUserInfo] = useState(null);
  const [advertiserAccounts, setAdAccounts] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [tempId, setTempId] = useState(null);

  // Multi-brand support
  const [brands, setBrands] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountBrandMapping, setAccountBrandMapping] = useState({});

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }

    setTempId(temp_id);
    loadTikTokAccounts(temp_id);
    fetchBrands();
  }, [searchParams]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const loadTikTokAccounts = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/tiktok/accounts', {
        params: { temp_id }
      });

      if (response.data.success) {
        setUserInfo(response.data.user_info);
        setAdAccounts(response.data.advertiser_accounts);
        setBrandId(response.data.brand_id);
      } else {
        setError(response.data.message || 'Failed to load TikTok accounts');
      }
    } catch (error) {
      console.error('Error loading TikTok accounts:', error);
      setError(error.response?.data?.message || 'Failed to load TikTok accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelection = (accountId, checked) => {
    if (checked) {
      const account = advertiserAccounts.find(a => a.advertiser_id === accountId);
      setSelectedAccounts([...selectedAccounts, account]);
      // For already-connected accounts, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth flow.
      const defaultBrand = account?.alreadyConnected && account?.connectedBrandId
        ? String(account.connectedBrandId)
        : brandId;
      setAccountBrandMapping(prev => ({
        ...prev,
        [accountId]: defaultBrand
      }));
    } else {
      setSelectedAccounts(selectedAccounts.filter(a => a.advertiser_id !== accountId));
      // Remove brand mapping
      setAccountBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[accountId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (accountId, newBrandId) => {
    setAccountBrandMapping(prev => ({
      ...prev,
      [accountId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedAccounts.length === 0) {
      setError('Please select at least one TikTok ad account');
      return;
    }

    // Validate brands
    const accountsWithoutBrands = selectedAccounts.filter(account => 
      !accountBrandMapping[account.advertiser_id]
    );
    if (accountsWithoutBrands.length > 0) {
      setError(`Please select a brand for all selected accounts`);
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const account of selectedAccounts) {
        const selectedBrandId = accountBrandMapping[account.advertiser_id] || brandId;

        // Get brand name from brands array
        const selectedBrand = brands.find(b => b.id === parseInt(selectedBrandId));
        const brandName = selectedBrand ? selectedBrand.name : 'Unknown Brand';

        // Format: TikTok Ads - {Brand Name} - {Account Name}
        const connectorName = `TikTok Ads - ${brandName} - ${account.name}`;

        const response = await api.post('/api/connectors/tiktok/create', {
          temp_id: tempId,
          advertiser_id: account.advertiser_id,
          account_name: account.name,
          connector_name: connectorName,
          brand_id: parseInt(selectedBrandId)
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${account.name}: ${response.data.message}`);
        }
      }
      
      // Clean up temporary OAuth data after all connectors are created
      try {
        await api.post('/api/connectors/tiktok/cleanup', { temp_id: tempId });
      } catch (cleanupError) {
        console.warn('Failed to cleanup temp data:', cleanupError);
        // Don't fail the whole operation if cleanup fails
      }

      // Get unique brand names
      const uniqueBrands = [...new Set(selectedAccounts.map(account => 
        getBrandName(accountBrandMapping[account.advertiser_id] || brandId)
      ))];

      navigate('/connectors', {
        state: {
          message: `Successfully created ${createdConnectors.length} TikTok connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          severity: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CLOSED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading TikTok ad accounts...</Typography>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, p: 3 }}>
        <Alert severity="success" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <CheckIcon sx={{ mr: 1 }} />
          <Box>
            <Typography variant="h6">{success}</Typography>
            <Typography variant="body2">
              Redirecting to connectors page...
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/connectors')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <TikTokIcon sx={{ color: '#1877F2', mr: 2, fontSize: 32 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            TikTok Ad Account Selection
          </Typography>
          {userInfo && (
            <Typography variant="body2" color="text.secondary">
              Authenticated as {userInfo.name} ({userInfo.email})
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Account Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select TikTok Ad Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose one or more TikTok ad accounts to connect. You can assign each account to a different brand.
          </Typography>

          <Grid container spacing={2}>
            {advertiserAccounts.map((account) => (
              <Grid item xs={12} md={6} key={account.advertiser_id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAccounts.some(a => a.advertiser_id === account.advertiser_id)}
                          onChange={(e) => handleAccountSelection(account.advertiser_id, e.target.checked)}
                          disabled={creating}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#1877F2', mr: 2 }}>
                              <TikTokIcon />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {account.advertiser_id}
                              </Typography>
                              {account.alreadyConnected && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={account.connectedBrandName
                                      ? `Connected · ${account.connectedBrandName}`
                                      : 'Already connected'}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip 
                              size="small" 
                              label={account.account_status || 'ACTIVE'} 
                              color={getStatusColor(account.account_status)}
                            />
                            {account.currency && (
                              <Typography variant="caption" color="text.secondary">
                                {account.currency}
                              </Typography>
                            )}
                            {account.timezone_name && (
                              <Typography variant="caption" color="text.secondary">
                                • {account.timezone_name}
                              </Typography>
                            )}
                          </Box>

                          {/* Brand Selection */}
                          {selectedAccounts.some(a => a.advertiser_id === account.advertiser_id) && (
                            <Box sx={{ mt: 2 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id={`brand-select-${account.advertiser_id}`}>Assign to Brand</InputLabel>
                                <Select
                                  labelId={`brand-select-${account.advertiser_id}`}
                                  value={accountBrandMapping[account.advertiser_id] || ''}
                                  label="Assign to Brand"
                                  onChange={(e) => handleBrandChange(account.advertiser_id, e.target.value)}
                                  disabled={creating}
                                  sx={{ borderRadius: 1 }}
                                >
                                  {brands.map(brand => (
                                    <MenuItem key={brand.id} value={brand.id}>
                                      {brand.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {advertiserAccounts.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedAccounts.length} account(s) selected
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/connectors')}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateConnectors}
              disabled={selectedAccounts.length === 0 || creating}
              startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                backgroundColor: '#1877F2',
                '&:hover': { backgroundColor: '#166FE5' }
              }}
            >
              {creating ? 'Creating...' : `Create ${selectedAccounts.length} Connector(s)`}
            </Button>
          </Box>
        </Box>
      )}

      {/* Info */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>What happens next?</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          • Your TikTok ad account will be connected securely
          • Data sync will be configured automatically
          • You can start analyzing your TikTok ad performance in the dashboard
        </Typography>
      </Alert>
    </Box>
  );
};

export default TikTokAccountSelection;
